import { useMemo, useState } from 'react';
import { BarChart } from '../components/Chart';
import { IconFlame, IconPlus } from '../components/Icons';
import { Sheet } from '../components/Sheet';
import {
  CARDIO_BY_ID,
  CARDIO_TYPES,
  estimateCalories,
  HR_ZONES,
  zoneRange,
} from '../data/cardio';
import { weeklyCardioTarget } from '../engine/nutrition';
import { planDay } from '../engine/planner';
import { dayShort, formatRelative, parseIso, startOfWeek, todayIso, weekKey } from '../lib/dates';
import { useApp } from '../store/AppStore';

export function Cardio() {
  const { state, deleteCardio } = useApp();
  const [logging, setLogging] = useState(false);
  const [showZones, setShowZones] = useState(false);

  const today = todayIso();
  const target = weeklyCardioTarget(state.profile);
  const suggestion = useMemo(() => planDay(state, today).cardio, [state, today]);

  const thisWeek = useMemo(() => {
    const key = weekKey(today);
    return state.cardio.filter((c) => weekKey(c.date) === key);
  }, [state.cardio, today]);

  const weekMinutes = thisWeek.reduce((n, c) => n + c.minutes, 0);
  const weekCalories = thisWeek.reduce((n, c) => n + c.calories, 0);

  const history = useMemo(
    () => [...state.cardio].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [state.cardio],
  );

  // Calories per day across the current week, for the bar chart.
  const weekBars = useMemo(() => {
    const monday = startOfWeek(today);
    return Array.from({ length: 7 }, (_, i) => {
      const d = parseIso(monday);
      d.setDate(d.getDate() + i);
      const iso = todayIso(d);
      const value = state.cardio
        .filter((c) => c.date === iso)
        .reduce((n, c) => n + c.calories, 0);
      return { label: dayShort(d.getDay()), value };
    });
  }, [state.cardio, today]);

  return (
    <div className="stack">
      <div className="card">
        <div className="card-title">This week</div>
        <div className="grid-3">
          <div className="stat">
            <div className="label">Sessions</div>
            <div className="value">
              {thisWeek.length}
              <span className="unit"> / {target.sessions}</span>
            </div>
          </div>
          <div className="stat">
            <div className="label">Minutes</div>
            <div className="value">
              {weekMinutes}
              <span className="unit"> / {target.minutes}</span>
            </div>
          </div>
          <div className="stat">
            <div className="label">Burned</div>
            <div className="value">
              {weekCalories}
              <span className="unit"> kcal</span>
            </div>
          </div>
        </div>
        <div className="bar" style={{ marginTop: 11 }}>
          <span
            style={{
              width: `${Math.min(100, (weekMinutes / target.minutes) * 100)}%`,
              background: 'var(--info)',
            }}
          />
        </div>
        <div className="note small" style={{ marginTop: 11 }}>
          {target.note}
        </div>
      </div>

      <button className="btn primary lg block" onClick={() => setLogging(true)}>
        <IconPlus /> Log a cardio session
      </button>

      {suggestion && (
        <div className="card">
          <div className="card-title">Suggested today</div>
          <div className="row between">
            <div className="grow">
              <h3>{suggestion.type.name}</h3>
              <div className="small muted">
                {suggestion.minutes} min · {suggestion.type.defaultIntensity}
              </div>
            </div>
            <span className="pill accent">
              <IconFlame size={13} />~{suggestion.estimatedCalories}
            </span>
          </div>
          <div className="note small" style={{ marginTop: 10 }}>
            {suggestion.type.bestFor}
          </div>
        </div>
      )}

      <div className="card">
        <div className="row between" style={{ marginBottom: 10 }}>
          <span className="card-title" style={{ margin: 0 }}>
            Calories by day
          </span>
          <button className="btn ghost sm" onClick={() => setShowZones(true)}>
            HR zones
          </button>
        </div>
        <BarChart points={weekBars} unit="" color="var(--info)" />
      </div>

      <div className="card flush">
        <div className="card-title" style={{ padding: '14px 14px 8px', marginBottom: 0 }}>
          History
        </div>
        {history.length === 0 && (
          <div className="empty small">
            Nothing logged yet. Tuesday and Thursday are your cardio days — log the
            first one and the weekly totals start filling in.
          </div>
        )}
        {history.slice(0, 40).map((entry) => {
          const type = CARDIO_BY_ID[entry.typeId];
          return (
            <div className="list-item" key={entry.id}>
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{type?.name ?? entry.typeId}</div>
                <div className="tiny faint">
                  {formatRelative(entry.date)} · {entry.minutes} min
                  {entry.distanceKm ? ` · ${entry.distanceKm} km` : ''}
                  {entry.avgHr ? ` · ${entry.avgHr} bpm` : ''}
                </div>
              </div>
              <span className={`pill${entry.caloriesEstimated ? '' : ' ok'}`}>
                {entry.calories} kcal
              </span>
              <button
                className="btn ghost sm"
                onClick={() => {
                  if (confirm('Delete this session?')) deleteCardio(entry.id);
                }}
                aria-label="Delete session"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      <LogSheet open={logging} onClose={() => setLogging(false)} />

      <Sheet open={showZones} title="Heart rate zones" onClose={() => setShowZones(false)}>
        <p className="small muted">
          Based on an estimated max of {208 - Math.round(0.7 * state.profile.age)} bpm for
          age {state.profile.age}. Estimates run about ±10 bpm either way, so treat
          the boundaries as soft.
        </p>
        <div className="stack" style={{ gap: 8, marginTop: 12 }}>
          {HR_ZONES.map((zone) => {
            const [lo, hi] = zoneRange(state.profile.age, zone);
            return (
              <div className="row between" key={zone.n} style={{ gap: 10 }}>
                <div className="grow">
                  <div style={{ fontWeight: 600 }}>
                    Zone {zone.n} — {zone.name}
                  </div>
                  <div className="tiny faint">{zone.purpose}</div>
                </div>
                <span className="pill mono">
                  {lo}–{hi}
                </span>
              </div>
            );
          })}
        </div>
      </Sheet>
    </div>
  );
}

function LogSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { state, addCardio } = useApp();
  const suggestion = useMemo(() => planDay(state).cardio, [state]);

  const [typeId, setTypeId] = useState(suggestion?.type.id ?? CARDIO_TYPES[0]!.id);
  const [date, setDate] = useState(todayIso());
  const [minutes, setMinutes] = useState(String(suggestion?.minutes ?? 30));
  const [distance, setDistance] = useState('');
  const [avgHr, setAvgHr] = useState('');
  const [calories, setCalories] = useState('');
  const [rpe, setRpe] = useState('');
  const [notes, setNotes] = useState('');

  const type = CARDIO_BY_ID[typeId];
  const mins = Number(minutes) || 0;
  const estimated = type ? estimateCalories(type.met, state.profile.bodyweightKg, mins) : 0;

  const reset = () => {
    setDistance('');
    setAvgHr('');
    setCalories('');
    setRpe('');
    setNotes('');
  };

  const submit = () => {
    if (!type || mins <= 0) return;
    const entered = Number(calories);
    addCardio({
      date,
      typeId,
      minutes: mins,
      distanceKm: distance ? Number(distance) : undefined,
      avgHr: avgHr ? Number(avgHr) : undefined,
      calories: entered > 0 ? entered : estimated,
      caloriesEstimated: !(entered > 0),
      rpe: rpe ? Number(rpe) : undefined,
      notes: notes.trim() || undefined,
    });
    reset();
    onClose();
  };

  return (
    <Sheet open={open} title="Log cardio" onClose={onClose}>
      <div className="stack">
        <div className="field">
          <label htmlFor="cardio-type">What did you do?</label>
          <select id="cardio-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            {CARDIO_TYPES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {type && <div className="hint">{type.defaultIntensity}</div>}
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="cardio-date">Date</label>
            <input
              id="cardio-date"
              type="date"
              value={date}
              max={todayIso()}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cardio-min">Minutes</label>
            <input
              id="cardio-min"
              type="number"
              inputMode="numeric"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="cardio-dist">Distance (km)</label>
            <input
              id="cardio-dist"
              type="number"
              inputMode="decimal"
              step="0.1"
              value={distance}
              placeholder="optional"
              onChange={(e) => setDistance(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cardio-hr">Avg HR</label>
            <input
              id="cardio-hr"
              type="number"
              inputMode="numeric"
              value={avgHr}
              placeholder="optional"
              onChange={(e) => setAvgHr(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="cardio-kcal">Calories</label>
          <input
            id="cardio-kcal"
            type="number"
            inputMode="numeric"
            value={calories}
            placeholder={`${estimated} (estimated)`}
            onChange={(e) => setCalories(e.target.value)}
          />
          <div className="hint">
            Leave blank to use the estimate. If the machine or your watch gives you a
            number, type that in instead — it will be closer than any formula.
          </div>
        </div>

        <div className="field">
          <label htmlFor="cardio-rpe">How hard was it? (1–10)</label>
          <input
            id="cardio-rpe"
            type="number"
            inputMode="numeric"
            min="1"
            max="10"
            value={rpe}
            placeholder="optional"
            onChange={(e) => setRpe(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="cardio-notes">Notes</label>
          <textarea
            id="cardio-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="optional"
          />
        </div>

        <button className="btn primary lg block" onClick={submit} disabled={mins <= 0}>
          Save session
        </button>
      </div>
    </Sheet>
  );
}
